import React, { useState } from 'react';
import { ArrowClockwise, Crown, EnvelopeSimple, Sparkle } from '@phosphor-icons/react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export const AI_SUBSCRIPTION_EMAIL = 'hello@galago.in';
export const AI_USAGE_IMAGE_GENERATION = 'image_generation';
export const AI_USAGE_IMAGE_READING = 'image_reading';
export const AI_USAGE_LUNA_TEXT = 'luna_text';

const USAGE_LABELS = {
  [AI_USAGE_IMAGE_GENERATION]: 'image creation',
  [AI_USAGE_IMAGE_READING]: 'image reading',
  [AI_USAGE_LUNA_TEXT]: 'AI writing',
};

const resetCountdown = (resetAt) => {
  const milliseconds = new Date(resetAt).getTime() - Date.now();
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) return 'a few moments';
  const totalMinutes = Math.max(1, Math.ceil(milliseconds / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours && minutes) return `${hours}h ${minutes}m`;
  if (hours) return `${hours}h`;
  return `${minutes}m`;
};

const contactHref = (featureName) => {
  const subject = 'GalaGo AI Premium subscription';
  const body = `Hello GalaGo team,\n\nI would like to subscribe to ${featureName || 'GalaGo AI Premium features'}.\n\nPlease share the next steps.`;
  return `mailto:${AI_SUBSCRIPTION_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

export function AIPremiumDialog({
  open,
  onOpenChange,
  featureName = 'this AI feature',
  onManualEntry,
  manualEntryLabel = 'Enter details manually',
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden border-amber-200 p-0 sm:max-w-md">
        <div className="bg-gradient-to-br from-amber-50 via-white to-violet-50 px-6 pb-5 pt-7">
          <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <Crown weight="fill" className="h-6 w-6" />
          </div>
          <DialogHeader>
            <DialogTitle className="text-xl">Subscribe for advanced AI features</DialogTitle>
            <DialogDescription className="pt-2 leading-6">
              {featureName} is part of GalaGo AI Premium. Contact the developer at{' '}
              <span className="font-medium text-foreground">{AI_SUBSCRIPTION_EMAIL}</span> to activate a subscription for your account.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-5 rounded-lg border border-amber-200/80 bg-white/80 p-4 text-sm text-muted-foreground">
            <div className="flex items-start gap-3">
              <Sparkle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" weight="fill" />
              <p>Online subscription checkout is coming soon. For now, we’ll help you subscribe over email.</p>
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 px-6 pb-6 sm:justify-stretch">
          {onManualEntry && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                onOpenChange(false);
                onManualEntry();
              }}
            >
              {manualEntryLabel}
            </Button>
          )}
          <Button asChild className="w-full bg-amber-600 text-white hover:bg-amber-700">
            <a href={contactHref(featureName)}>
              <EnvelopeSimple className="h-4 w-4" />
              Contact developer
            </a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AIUsageLimitDialog({
  open,
  onOpenChange,
  usageCategory,
  resetAt,
  onManualEntry,
  manualEntryLabel = 'Enter details manually',
}) {
  const [, rerender] = useState(0);

  React.useEffect(() => {
    if (!open) return undefined;
    const timer = window.setInterval(() => rerender((value) => value + 1), 60000);
    return () => window.clearInterval(timer);
  }, [open]);

  const label = USAGE_LABELS[usageCategory] || 'AI';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-violet-200 sm:max-w-md">
        <DialogHeader>
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-violet-100 text-violet-700">
            <Sparkle className="h-5 w-5" weight="fill" />
          </div>
          <DialogTitle>More AI access is coming soon</DialogTitle>
          <DialogDescription className="pt-2 leading-6">
            You've used today's {label} allowance. More becomes available in{' '}
            <span className="font-medium text-foreground">{resetCountdown(resetAt)}</span>.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
          Your allowance refreshes automatically every day. Premium access removes daily limits.
        </div>
        <DialogFooter className="gap-2">
          {onManualEntry && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                onManualEntry();
              }}
            >
              {manualEntryLabel}
            </Button>
          )}
          <Button asChild variant="outline">
            <a href={contactHref('unlimited GalaGo AI access')}>Explore unlimited access</a>
          </Button>
          <Button type="button" onClick={() => onOpenChange(false)}>Got it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AIPremiumPageWall({
  featureName = 'this AI feature',
  onRefresh,
  refreshing = false,
}) {
  return (
    <div className="mx-auto flex min-h-[65vh] w-full max-w-2xl items-center justify-center px-4 py-10">
      <div className="w-full overflow-hidden rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-violet-50 p-7 text-center shadow-sm sm:p-10">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <Crown weight="fill" className="h-7 w-7" />
        </div>
        <h1 className="mt-5 text-2xl font-semibold text-foreground">Subscribe for advanced AI features</h1>
        <p className="mx-auto mt-3 max-w-lg text-sm leading-6 text-muted-foreground">
          {featureName} is part of GalaGo AI Premium. Contact the developer at{' '}
          <span className="font-medium text-foreground">{AI_SUBSCRIPTION_EMAIL}</span> to activate access for your account.
        </p>
        <div className="mt-6 flex flex-col justify-center gap-2 sm:flex-row">
          <Button asChild className="bg-amber-600 text-white hover:bg-amber-700">
            <a href={contactHref(featureName)}>
              <EnvelopeSimple className="h-4 w-4" />
              Contact developer
            </a>
          </Button>
          {onRefresh && (
            <Button type="button" variant="outline" onClick={onRefresh} disabled={refreshing}>
              <ArrowClockwise className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Checking access' : 'Check access again'}
            </Button>
          )}
        </div>
        <p className="mt-4 text-xs text-muted-foreground">Online subscription checkout is coming soon.</p>
      </div>
    </div>
  );
}

export default function AIPremiumButton({
  featureName,
  usageCategory,
  children,
  className,
  onManualEntry,
  manualEntryLabel,
  onClick: _onClick,
  disabled: _disabled,
  type = 'button',
  ...buttonProps
}) {
  const [limitOpen, setLimitOpen] = useState(false);
  const {
    hasAiPremiumAccess,
    aiSubscription,
    aiSubscriptionLoading,
    refreshAiSubscription,
  } = useAuth();
  const usage = usageCategory ? aiSubscription?.usage?.[usageCategory] : null;

  const refreshUsageAfterAction = (result, previousRemaining) => {
    if (!usageCategory || hasAiPremiumAccess || !result?.then) return;
    Promise.resolve(result).then(async () => {
      const status = await refreshAiSubscription?.();
      const remaining = status?.usage?.[usageCategory]?.remaining;
      if (!Number.isFinite(remaining) || !Number.isFinite(previousRemaining) || remaining >= previousRemaining) return;
      const lowThreshold = usageCategory === AI_USAGE_LUNA_TEXT ? 5 : 1;
      if (remaining <= lowThreshold && remaining > 0) {
        toast.message(`${remaining} ${USAGE_LABELS[usageCategory]} action${remaining === 1 ? '' : 's'} available today.`);
      }
    }).catch(() => {});
  };

  return (
    <>
      <Button
        type={type}
        className={cn('group relative', className)}
        disabled={_disabled || aiSubscriptionLoading}
        onClick={(event) => {
          if (!hasAiPremiumAccess && usageCategory && usage?.remaining === 0) {
            event.preventDefault();
            event.stopPropagation();
            setLimitOpen(true);
            return;
          }
          const result = _onClick?.(event);
          refreshUsageAfterAction(result, usage?.remaining);
        }}
        aria-label={typeof children === 'string' ? children : featureName || 'AI feature'}
        title={hasAiPremiumAccess ? undefined : 'Daily AI allowance applies; Premium removes daily limits'}
        {...buttonProps}
      >
        {!hasAiPremiumAccess && <Crown className="h-4 w-4 text-amber-500" weight="fill" />}
        {children}
      </Button>
      <AIUsageLimitDialog
        open={limitOpen}
        onOpenChange={setLimitOpen}
        usageCategory={usageCategory}
        resetAt={aiSubscription?.reset_at}
        onManualEntry={onManualEntry}
        manualEntryLabel={manualEntryLabel}
      />
    </>
  );
}
